'use client';

import { analyticsConfig } from '@eliadmin/core/src/components/analytics/analytics-config';
import { env } from '@eliadmin/core/src/env';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useEffect, useRef } from 'react';

export const trackEvent = (eventName: string, eventParams: Record<string, any> = {}) => {
  const eventWithTimestamp = {
    ...eventParams,
    timestamp: new Date().toISOString(),
  };

  // Use both gtag and dataLayer when available
  if (window.dataLayer && analyticsConfig.dataLayerEnabled) {
    window.dataLayer.push({ event: eventName, ...eventWithTimestamp });
  }
  if (window.gtag && analyticsConfig.gtagEnabled) {
    window.gtag('event', eventName, eventWithTimestamp);
  }
};

const getFullUrl = (path: string): string => {
  if (typeof window !== 'undefined') {
    return new URL(path, window.location.origin).href;
  }
  return new URL(path, env.NEXT_PUBLIC_API_URL).href;
};

export const trackPageView = (path: string) => {
  const fullUrl = getFullUrl(path);
  const pageViewData = {
    page_location: fullUrl,
    page_path: path,
    page_title: document.title,
    timestamp: new Date().toISOString(),
  };

  if (window.gtag) {
    window.gtag('event', 'page_view', pageViewData);
  }
  if (window.dataLayer) {
    window.dataLayer.push({ event: 'page_view', ...pageViewData });
  }
};

export const trackEcommerce = {
  viewItem: (item: { id: string; name: string; price: number; currency: string }) => {
    trackEvent('view_item', {
      currency: item.currency,
      value: item.price,
      item_id: item.id,
      item_name: item.name,
      price: item.price,
      quantity: 1,
    });
  },
  addToCart: (item: {
    id: string;
    name: string;
    price: number;
    currency: string;
    send_to?: string | null;
  }) => {
    trackEvent('add_to_cart', {
      currency: item.currency,
      value: item.price,
      item_id: item.id,
      item_name: item.name,
      price: item.price,
      quantity: 1,
      send_to: item.send_to,
    });
  },
  beginCheckout: (cart: {
    currency: string;
    total: number;
    items: {
      item_id: string;
      item_name: string;
      price: number;
      quantity: number;
    }[];
    send_to: string;
  }) => {
    const cartItems = cart.items.map((item, index) => {
      return {
        index,
        item_id: item.item_id,
        item_name: item.item_name,
        price: item.price,
        quantity: item.quantity,
      };
    });
    trackEvent('begin_checkout', {
      currency: cart.currency,
      value: cart.total,
      items: cartItems,
      send_to: cart.send_to,
    });
  },
  purchase: (order: {
    identifier: string;
    price: number;
    currency: string;
    price_without_vat: number;
    shipment_price_with_vat: number;
    items: Array<{
      item_id: string;
      item_name: string;
      price: number;
      quantity: number;
      [key: string]: any;
    }>;
    affiliation?: string;
    coupon?: string;
  }) => {
    const items = order.items.map(item => ({ ...item }));

    trackEvent('purchase', {
      ecommerce: {
        currency: order.currency,
        value: order.price,
        transaction_id: order.identifier,
        affiliation: order.affiliation ?? '',
        tax: order.price - order.price_without_vat,
        shipping: order.shipment_price_with_vat,
        coupon: order.coupon ?? '',
        items,
      },
    });
  },
  conversion: (order: {
    send_to: string;
    value: number;
    currency: string;
    transaction_id: string;
  }) => {
    trackEvent('conversion', {
      send_to: order.send_to,
      value: order.value,
      currency: order.currency,
      transaction_id: order.transaction_id,
    });
  },
};

export const trackUserActions = {
  // Clicking a phone link (GA4: select_content with content_type: 'phone')
  clickPhone: (phone: string, extras?: { link_id?: string; link_url?: string }) => {
    trackEvent('select_content', {
      content_type: 'phone',
      phone,
      link_id: extras?.link_id,
      link_url: extras?.link_url,
    });
  },
  // Clicking an email link (GA4: select_content with content_type: 'email')
  clickEmail: (email: string, extras?: { link_id?: string; link_url?: string }) => {
    trackEvent('select_content', {
      content_type: 'email',
      email,
      link_id: extras?.link_id,
      link_url: extras?.link_url,
    });
  },
  // Contact form submit (use GA4 recommended 'generate_lead')
  contactFormSubmit: (params?: {
    form_id?: string;
    subject?: string;
    name?: string;
    email?: string;
    phone?: string;
    location?: string;
  }) => {
    trackEvent('generate_lead', {
      form_id: params?.form_id,
      subject: params?.subject,
      name: params?.name,
      email: params?.email,
      phone: params?.phone,
      location: params?.location,
    });
  },
  // Newsletter subscription → GA4: sign_up with method 'newsletter'
  newsletterSubscribe: (params?: { email?: string; list?: string; method?: string }) => {
    trackEvent('sign_up', {
      method: 'newsletter',
      email: params?.email,
      list: params?.list,
    } as any);
  },
  // Registration (GA4 recommended 'sign_up')
  registration: (params?: { method?: string; user_type?: string; email?: string }) => {
    trackEvent('sign_up', {
      method: params?.method || 'form',
      user_type: params?.user_type,
      email: params?.email,
    });
  },
  // Login (GA4: login)
  login: (params?: { method?: string }) => {
    trackEvent('login', {
      method: params?.method || 'form',
    });
  },
  // Search (GA4: search)
  search: (query: string, extras?: Record<string, any>) => {
    trackEvent('search', {
      search_term: query,
      ...extras,
    });
  },
  // Share (GA4: share)
  share: (params: {
    method?: string;
    content_type?: string;
    item_id?: string;
    link_url?: string;
  }) => {
    trackEvent('share', {
      method: params?.method,
      content_type: params?.content_type,
      item_id: params?.item_id,
      link_url: params?.link_url,
    });
  },
  // File download (GA4: file_download)
  fileDownload: (params: { file_name?: string; link_url?: string }) => {
    trackEvent('file_download', {
      file_name: params?.file_name,
      link_url: params?.link_url,
    });
  },
  // Menu interaction (GA4: select_content)
  menuSelect: (params: { item_id?: string; item_name?: string }) => {
    trackEvent('select_content', {
      content_type: 'menu',
      item_id: params?.item_id,
      item_name: params?.item_name,
    });
  },
  // Custom event: student sends interest in a job offer
  sendInterest: (params: {
    user_id?: string | number;
    user_name?: string;
    user_email?: string;
    offer_id?: string | number;
    offer_name?: string;
    company_id?: string | number;
    company_name?: string;
  }) => {
    trackEvent('send_interest', {
      event_category: 'jobportal',
      event_label: 'send_interest',
      content_type: 'job_offer_interest',
      user_id: params.user_id,
      user_name: params.user_name,
      user_email: params.user_email,
      offer_id: params.offer_id,
      offer_name: params.offer_name,
      company_id: params.company_id,
      company_name: params.company_name,
    });
  },
};

type TrackComponentProps = {
  eventName:
    | 'conversion'
    | 'purchase'
    | 'view_item'
    | 'begin_checkout'
    | 'add_to_cart'
    | 'select_content'
    | 'generate_lead'
    | 'sign_up'
    | 'login'
    | 'search'
    | 'share'
    | 'file_download';
  eventParams: Record<string, any | any[]>;
};

export const TrackComponent = ({ eventName, eventParams }: TrackComponentProps) => {
  const generalLayout = useGlobalStore(s => s.generalLayout);
  const didTrackRef = useRef(false);

  const hasTracked = (key: string) => {
    try {
      return typeof window !== 'undefined' && !!window.localStorage.getItem(key);
    } catch {
      return false;
    }
  };

  const markTracked = (key: string) => {
    try {
      if (typeof window !== 'undefined') window.localStorage.setItem(key, '1');
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (didTrackRef.current) return;
    didTrackRef.current = true;

    // Start tracking logic after 3 seconds
    let markTimeoutId: number | undefined;
    const startTimeoutId = window.setTimeout(() => {
      const send_to = generalLayout?.marketing?.gtm_id;

      // dedupe once per transaction for purchase/conversion
      const transactionId =
        (eventParams && (eventParams as any).transaction_id) ||
        (eventParams && (eventParams as any).identifier);
      const shouldDedupe =
        (eventName === 'purchase' || eventName === 'conversion') && !!transactionId;
      const storageKey = shouldDedupe
        ? `ea:tracked:${eventName}:${String(transactionId)}`
        : undefined;

      if (storageKey && hasTracked(storageKey)) {
        return;
      }

      trackEvent(eventName, { ...eventParams, ...(send_to ? { send_to } : {}) });

      // Delay marking as tracked to allow co-mounted trackers (e.g., purchase + conversion)
      // to both fire before dedupe prevents the second one. Also helps first-load race conditions.
      if (storageKey) {
        markTimeoutId = window.setTimeout(() => {
          markTracked(storageKey!);
        }, 1000);
      }
    }, 2000);
    return () => {
      if (startTimeoutId) clearTimeout(startTimeoutId);
      if (markTimeoutId) clearTimeout(markTimeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};
